import { z } from 'zod/v4';

/**
 * Типы действий аудит-лога
 */
export const AUDIT_ACTIONS = [
  'CREATE',
  'READ',
  'UPDATE',
  'DELETE',
  'LOGIN',
  'LOGOUT',
  'EXPORT',
  'IMPORT',
  'ACTIVATE',
  'DEACTIVATE',
  'APPROVE',
  'REJECT',
  'SEND',
  'CANCEL',
  'CUSTOM',
] as const;

export type AuditAction = (typeof AUDIT_ACTIONS)[number];

/**
 * Человекопонятные названия действий
 */
export const ACTION_LABELS: Record<AuditAction, string> = {
  CREATE: 'Создание',
  READ: 'Просмотр',
  UPDATE: 'Обновление',
  DELETE: 'Удаление',
  LOGIN: 'Вход',
  LOGOUT: 'Выход',
  EXPORT: 'Экспорт',
  IMPORT: 'Импорт',
  ACTIVATE: 'Активация',
  DEACTIVATE: 'Деактивация',
  APPROVE: 'Одобрение',
  REJECT: 'Отклонение',
  SEND: 'Отправка',
  CANCEL: 'Отмена',
  CUSTOM: 'Другое',
};

/**
 * Человекопонятные названия типов сущностей
 */
export const ENTITY_TYPE_LABELS: Record<string, string> = {
  // Users / Access
  User: 'Пользователь',
  AdminUser: 'Администратор',
  AdminSession: 'Сессия администратора',
  PhoneAuth: 'Авторизация по телефону',
  UserAccess: 'Доступ пользователя',
  UserRole: 'Роль пользователя',
  Role: 'Роль',
  Permission: 'Право доступа',
  RolePermission: 'Право роли',
  E2EE_IDENTITY: 'E2EE-идентичность',

  // Catalog: courses / modules / books
  Course: 'Курс',
  Module: 'Модуль',
  ModuleItem: 'Элемент модуля',
  Article: 'Статья',
  Book: 'Книга',
  Teacher: 'Учитель',

  // Content / taxonomy
  Banner: 'Баннер',
  Category: 'Категория',
  Tag: 'Тег',

  // Commerce / payments
  Order: 'Заказ',
  OrderItem: 'Позиция заказа',
  Payment: 'Платёж',
  PaymentEvent: 'Событие платежа',
  Product: 'Товар',
  Subscription: 'Подписка',
  SubscriptionPlan: 'План подписки',
  SubscriptionBenefit: 'Привилегия плана',
  ChannelPass: 'Подписка в канал',
  PromoCode: 'Промокод',
  Discount: 'Скидка',
  FamilyMember: 'Член семьи',

  // Support / livestreams / calls
  SupportTicket: 'Обращение в поддержку',
  Livestream: 'Трансляция',
  BreakoutSession: 'Сессионный зал',
  Call: 'Звонок',

  // Telegram
  TelegramChannel: 'Telegram канал',
  TelegramBot: 'Telegram бот',
  TelegramSession: 'Telegram сессия',
  ChannelMembership: 'Членство в канале',

  // Messaging / notifications
  Trigger: 'Триггер',
  Broadcast: 'Рассылка',
  NotificationTemplate: 'Шаблон уведомления',
  MessageTemplate: 'Шаблон сообщения',

  // Knowledge base / AI
  KnowledgeDocument: 'Документ базы знаний',
  AIChat: 'AI чат',
  AIChatMessage: 'AI сообщение',
  AITranslation: 'AI перевод',
  AIConfig: 'AI конфигурация',

  // Files
  File: 'Файл',
  FileLink: 'Связь файла',

  // User-personal entities
  Goal: 'Цель',
  Intention: 'Намерение',
  Reminder: 'Напоминание',

  // Other
  Event: 'Событие',
  Language: 'Язык',
  TranslationKey: 'Ключ перевода',
  VideoAccess: 'Доступ к видео',
  ApiKey: 'API ключ',
  BotToken: 'Токен бота',
};

/**
 * Типы акторов
 */
export const ACTOR_TYPES = ['ADMIN', 'USER', 'SYSTEM'] as const;
export type ActorType = (typeof ACTOR_TYPES)[number];

/**
 * Человекопонятные названия типов акторов
 */
export const ACTOR_TYPE_LABELS: Record<ActorType, string> = {
  ADMIN: 'Администратор',
  USER: 'Пользователь',
  SYSTEM: 'Система',
};

/**
 * Схема запроса для списка аудит-логов
 */
export const auditLogsQuerySchema = z.object({
  page: z.coerce.number().min(1, 'Страница должна быть >= 1').default(1),
  limit: z.coerce.number().min(1, 'Лимит должен быть >= 1').max(100, 'Лимит должен быть <= 100').default(20),
  sortBy: z.enum(['createdAt', 'action', 'entityType', 'adminId', 'actorType']).default('createdAt'),
  sortOrder: z.enum(['asc', 'desc']).default('desc'),
  search: z.string().optional(),
  action: z.string().optional(),
  entityType: z.string().optional(),
  adminId: z.coerce.number().optional(),
  userId: z.coerce.number().optional(),
  actorType: z.enum(['ADMIN', 'USER', 'SYSTEM']).optional(),
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
});

export type AuditLogsQueryDto = z.infer<typeof auditLogsQuerySchema>;

/**
 * Тип аудит-лога с дополнительными полями
 */
export interface AuditLogWithAdmin {
  id: number;
  adminId: number | null;
  userId: number | null;
  actorType: ActorType;
  action: string;
  entityType: string;
  entityId: string;
  oldData: Record<string, unknown> | null;
  newData: Record<string, unknown> | null;
  ipAddress: string | null;
  userAgent: string | null;
  createdAt: Date;
  admin: {
    id: number | null;
    username: string;
    email: string;
  } | null;
  user: {
    id: number | null;
    firstName: string | null;
    lastName: string | null;
    username: string | null;
  } | null;
}

/**
 * Тип для форматированного лога
 */
export interface FormattedAuditLog extends AuditLogWithAdmin {
  actionLabel: string;
  entityTypeLabel: string;
  actorTypeLabel: string;
  description: string;
  changes: FieldChange[] | null;
}

/**
 * Тип для изменения поля
 */
export interface FieldChange {
  field: string;
  fieldLabel: string;
  oldValue: unknown;
  newValue: unknown;
}

/**
 * Человекопонятные названия полей
 */
export const FIELD_LABELS: Record<string, string> = {
  id: 'ID',
  email: 'Email',
  username: 'Имя пользователя',
  firstName: 'Имя',
  lastName: 'Фамилия',
  phone: 'Телефон',
  password: 'Пароль',
  passwordHash: 'Пароль',
  title: 'Заголовок',
  description: 'Описание',
  content: 'Содержимое',
  status: 'Статус',
  isActive: 'Активен',
  isPublished: 'Опубликован',
  publishedAt: 'Дата публикации',
  startDate: 'Дата начала',
  endDate: 'Дата окончания',
  price: 'Цена',
  amount: 'Сумма',
  currency: 'Валюта',
  roleId: 'Роль',
  permissions: 'Права',
  imageUrl: 'Изображение',
  thumbnailUrl: 'Превью',
  videoUrl: 'Видео',
  url: 'URL',
  slug: 'Slug',
  order: 'Порядок',
  sortOrder: 'Порядок сортировки',
  categoryId: 'Категория',
  tags: 'Теги',
  duration: 'Длительность',
  durationDays: 'Длительность (дни)',
  maxUsers: 'Макс. пользователей',
  type: 'Тип',
  code: 'Код',
  displayName: 'Отображаемое имя',
  name: 'Название',
  createdAt: 'Дата создания',
  updatedAt: 'Дата обновления',
  deletedAt: 'Дата удаления',
  expiresAt: 'Истекает',
  scheduledAt: 'Запланировано на',
  sentAt: 'Отправлено',
  metadata: 'Метаданные',
  settings: 'Настройки',
  config: 'Конфигурация',
  options: 'Опции',
  features: 'Функции',
  benefits: 'Преимущества',
  conditions: 'Условия',
  rules: 'Правила',
  template: 'Шаблон',
  message: 'Сообщение',
  subject: 'Тема',
  body: 'Тело',
  priority: 'Приоритет',
  level: 'Уровень',
  progress: 'Прогресс',
  completed: 'Завершено',
  isCompleted: 'Завершено',
  isPaid: 'Оплачено',
  isFree: 'Бесплатно',
  isDefault: 'По умолчанию',
  isSystem: 'Системный',
  isEnabled: 'Включено',
  telegramId: 'Telegram ID',
  telegramUsername: 'Telegram логин',
  channelId: 'ID канала',
  chatId: 'ID чата',
  locale: 'Язык',
  timezone: 'Часовой пояс',
  country: 'Страна',
  city: 'Город',
  address: 'Адрес',
  notes: 'Заметки',
  comment: 'Комментарий',
  reason: 'Причина',
};
